import { useEffect, useState } from "react";
import {
  useAppSelector,
  useContracts,
  useCatalogItems,
  useDisplayedCollections,
  useTools,
} from "../app/hooks";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  OutlinedInput,
  Divider,
  Box,
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import ToolCard from "./tools/ToolCard";
import ContractCard from "./contracts/ContractCard";
import { Contract, DisplayedCollection, Role, Tool } from "../app/backendTypes";
import DatasetsCard from "./datasets/DatasetsCard";
import CatalogCard from "./catalog/CatalogCard";

export default function Home() {
  const { tools } = useTools();
  const { collections } = useDisplayedCollections();
  const { contracts } = useContracts();
  const { catalogItems } = useCatalogItems();

  const isAdminOrEditor = useAppSelector(
    (state) =>
      state.auth.roles.includes(Role.Administrator) ||
      state.auth.roles.includes(Role.CatalogEditor)
  );

  const userLegalUnitId = useAppSelector(
    (state) => state.auth.workingForLegalUnit
  );
  const ECONOMIC_LEGAL_UNIT_ID = 110;

  const [search, setSearch] = useState("");

  const [filteredTools, setFilteredTools] = useState(tools);
  const [filteredCollections, setFilteredCollections] = useState(collections);
  const [filteredContracts, setFilteredContracts] = useState(contracts);
  const [filteredCatalogItems, setFilteredCatalogItems] =
    useState(catalogItems);

  useEffect(() => {
    const CatalogItemFiltering = (
      approvedCompaniesIds: number[] | undefined,
      restrictedCompaniesIds: number[] | undefined
    ) => {
      if (isAdminOrEditor) return true;

      if (userLegalUnitId === null) return false;

      if (
        (approvedCompaniesIds === undefined ||
          approvedCompaniesIds.length === 0) &&
        (restrictedCompaniesIds === undefined ||
          restrictedCompaniesIds.length === 0)
      )
        return true;

      if (
        approvedCompaniesIds !== undefined &&
        restrictedCompaniesIds !== undefined
      )
        return (
          approvedCompaniesIds.includes(userLegalUnitId) &&
          !restrictedCompaniesIds.includes(userLegalUnitId)
        );

      if (
        restrictedCompaniesIds !== undefined &&
        restrictedCompaniesIds.includes(userLegalUnitId)
      )
        return false;

      if (
        approvedCompaniesIds !== undefined &&
        approvedCompaniesIds.includes(userLegalUnitId)
      )
        return true;
      return false;
    };

    const EconomicToolFiltering = (legalUnitId: number | null, tool: Tool) => {
      if (legalUnitId === null) {
        return false;
      }

      if (legalUnitId !== ECONOMIC_LEGAL_UNIT_ID) {
        return true;
      }

      const allowedTools = [
        Tool.BlackDuck,
        Tool.Jira,
        Tool.Confluence,
        Tool.Lucid,
        Tool.Mural,
        Tool.Polaris,
        Tool.Tableau,
        Tool.Udemy,
      ];

      return allowedTools.includes(tool);
    };

    setFilteredTools(
      tools.filter(
        (t) =>
          (t.kind.toLowerCase().includes(search) ||
            t.item.tool?.display_name.toLowerCase().includes(search) ||
            t.item.tool?.short_desc.toLowerCase().includes(search)) &&
          EconomicToolFiltering(userLegalUnitId, t.kind)
      )
    );

    setFilteredCollections(
      collections.filter(
        (c) =>
          (c.info.display_name.toLowerCase().includes(search) ||
            c.info.short_desc.toLowerCase().includes(search)) &&
          userLegalUnitId !== ECONOMIC_LEGAL_UNIT_ID
      )
    );

    setFilteredContracts(
      contracts.filter(
        (c) =>
          (c.name.toLowerCase().includes(search) ||
            c.short_desc.toLowerCase().includes(search) ||
            c.partner.toLowerCase().includes(search) ||
            c.tags?.some((t) => t.toLowerCase().includes(search))) &&
          tools.findIndex((t) => t.item.tool?.contract_id === c.id) === -1 &&
          catalogItems.findIndex((ci) => ci.contract_id === c.id) === -1 &&
          userLegalUnitId !== ECONOMIC_LEGAL_UNIT_ID
      )
    );

    setFilteredCatalogItems(
      catalogItems.filter((c) => {
        const contract = contracts.find(
          (contract) => contract.id === c.contract_id
        );

        const contractMatch =
          contract?.name.toLowerCase().includes(search) ||
          contract?.short_desc.toLowerCase().includes(search) ||
          contract?.partner.toLowerCase().includes(search) ||
          contract?.tags?.some((t) => t.toLowerCase().includes(search));

        const catalogItemMatch =
          c.info.display_name.toLowerCase().includes(search) ||
          c.info.short_desc.toLowerCase().includes(search);

        const companyMatch = CatalogItemFiltering(
          c.company_permissions?.approved_ids,
          c.company_permissions?.restricted_ids
        );

        return (contractMatch || catalogItemMatch) && companyMatch;
      })
    );
  }, [
    collections,
    contracts,
    catalogItems,
    search,
    tools,
    isAdminOrEditor,
    userLegalUnitId,
  ]);

  return (
    <>
      <FormControl>
        <InputLabel>Search</InputLabel>
        <OutlinedInput
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value.toLowerCase())}
          endAdornment={
            <IconButton
              onClick={() => setSearch("")}
              sx={{ visibility: search.length > 0 ? "visible" : "hidden" }}
            >
              <Clear />
            </IconButton>
          }
        />
      </FormControl>

      <Grid container spacing={4} justifyContent="center">
        {filteredTools.map((tool, i) => (
          <Grid size="auto" key={i}>
            <ToolCard toolConfig={tool} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={4} justifyContent="center">
        {filteredCatalogItems.map((c, i) => (
          <Grid size="auto" key={i}>
            <CatalogCard catalog={c} />
          </Grid>
        ))}
      </Grid>

      {filteredCollections.length > 0 && (
        <Box sx={{ width: "100%" }}>
          <Divider>Datasets</Divider>
        </Box>
      )}
      <Grid container spacing={4} justifyContent="center">
        {filteredCollections.map((collection, i) => (
          <Grid size="auto" key={i}>
            <DatasetsCard
              displayedCollection={collection as DisplayedCollection}
            />
          </Grid>
        ))}
      </Grid>

      {filteredContracts.length > 0 && (
        <Box sx={{ width: "100%" }}>
          <Divider>Corporate Contracts</Divider>
        </Box>
      )}
      <Grid container spacing={4} justifyContent="center">
        {filteredContracts.map((contract, i) => (
          <Grid size="auto" key={i}>
            <ContractCard contract={contract as Contract} />
          </Grid>
        ))}
      </Grid>
    </>
  );
}
